/* ==========================================================================
   MP Editorial — shared behaviour for the mp-editorial-* section family.

   Loaded by each section with a plain deferred <script src>. Several tags
   pointing at the same URL each execute, so everything below is guarded and
   idempotent; re-running only picks up newly added nodes.

   Provides:
     - scroll reveal for [data-ed-reveal]
     - one shared lightbox for every [data-ed-lb-group] on the page
     - poster/play handling for [data-ed-video]
   ========================================================================== */
(function () {
  'use strict';

  if (window.MPEditorial && window.MPEditorial.refresh) {
    window.MPEditorial.refresh();
    return;
  }

  var prefersReduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----------------------------------------------------------------------
     Reveal
     ---------------------------------------------------------------------- */

  var revealObserver = null;

  function initReveals(root) {
    var nodes = (root || document).querySelectorAll('[data-ed-reveal]:not([data-ed-reveal-bound])');
    if (!nodes.length) return;

    if (prefersReduced || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(nodes, function (el) {
        el.setAttribute('data-ed-reveal-bound', '');
        el.classList.add('is-revealed');
      });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.06, rootMargin: '0px 0px -8% 0px' }
      );
    }

    Array.prototype.forEach.call(nodes, function (el) {
      el.setAttribute('data-ed-reveal-bound', '');
      revealObserver.observe(el);
    });
  }

  /* ----------------------------------------------------------------------
     Lightbox

     One overlay serves every gallery. Opening reads the clicked item's
     sibling set out of the DOM, so galleries added later work with no
     extra wiring.
     ---------------------------------------------------------------------- */

  var lb = null;
  var lbItems = [];
  var lbIndex = 0;
  var lbTrigger = null;

  var ICON_PREV =
    '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true"><path d="M15 4 7 12l8 8"/></svg>';
  var ICON_NEXT =
    '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true"><path d="m9 4 8 8-8 8"/></svg>';
  var ICON_CLOSE =
    '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true"><path d="M5 5l14 14M19 5 5 19"/></svg>';

  function buildLightbox() {
    if (lb) return lb;

    lb = document.createElement('div');
    lb.className = 'mp-ed-lb';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'Photo viewer');
    lb.innerHTML =
      '<button class="mp-ed-lb__close" type="button" aria-label="Close photo viewer">' +
      ICON_CLOSE +
      '</button>' +
      '<button class="mp-ed-lb__nav mp-ed-lb__nav--prev" type="button" aria-label="Previous photo">' +
      ICON_PREV +
      '</button>' +
      '<figure class="mp-ed-lb__figure"><img class="mp-ed-lb__img" src="" alt=""></figure>' +
      '<button class="mp-ed-lb__nav mp-ed-lb__nav--next" type="button" aria-label="Next photo">' +
      ICON_NEXT +
      '</button>' +
      '<div class="mp-ed-lb__bar">' +
      '<span class="mp-ed-lb__caption"></span>' +
      '<span class="mp-ed-lb__count" aria-live="polite"></span>' +
      '</div>';

    document.body.appendChild(lb);

    lb.querySelector('.mp-ed-lb__close').addEventListener('click', closeLightbox);
    lb.querySelector('.mp-ed-lb__nav--prev').addEventListener('click', function (e) {
      e.stopPropagation();
      step(-1);
    });
    lb.querySelector('.mp-ed-lb__nav--next').addEventListener('click', function (e) {
      e.stopPropagation();
      step(1);
    });

    // Clicking the backdrop (but not the photo or the chrome) closes.
    lb.addEventListener('click', function (e) {
      if (e.target === lb || e.target.classList.contains('mp-ed-lb__figure')) closeLightbox();
    });

    return lb;
  }

  function render() {
    var item = lbItems[lbIndex];
    if (!item) return;

    var img = lb.querySelector('.mp-ed-lb__img');
    img.src = item.getAttribute('data-ed-lb-src') || '';
    img.alt = item.getAttribute('data-ed-lb-alt') || '';

    lb.querySelector('.mp-ed-lb__caption').textContent =
      item.getAttribute('data-ed-lb-caption') || '';
    lb.querySelector('.mp-ed-lb__count').textContent =
      pad(lbIndex + 1) + ' / ' + pad(lbItems.length);

    var multiple = lbItems.length > 1;
    lb.querySelector('.mp-ed-lb__nav--prev').hidden = !multiple;
    lb.querySelector('.mp-ed-lb__nav--next').hidden = !multiple;
  }

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function step(delta) {
    if (!lbItems.length) return;
    lbIndex = (lbIndex + delta + lbItems.length) % lbItems.length;
    render();
  }

  function openLightbox(item) {
    var group = item.closest('[data-ed-lb-group]');
    if (!group) return;

    lbItems = Array.prototype.slice.call(group.querySelectorAll('[data-ed-lb-src]'));
    lbIndex = lbItems.indexOf(item);
    if (lbIndex < 0) lbIndex = 0;

    lbTrigger = item;
    buildLightbox();
    render();

    lb.classList.add('is-open');
    // Next frame, so the opacity transition has a starting value to run from.
    requestAnimationFrame(function () {
      lb.classList.add('is-visible');
    });

    document.body.style.overflow = 'hidden';
    lb.querySelector('.mp-ed-lb__close').focus();
  }

  function closeLightbox() {
    if (!lb || !lb.classList.contains('is-open')) return;

    lb.classList.remove('is-visible');
    lb.classList.remove('is-open');
    lb.querySelector('.mp-ed-lb__img').src = '';
    document.body.style.overflow = '';

    if (lbTrigger) {
      lbTrigger.focus();
      lbTrigger = null;
    }
  }

  document.addEventListener('click', function (e) {
    var item = e.target.closest && e.target.closest('[data-ed-lb-src]');
    if (!item) return;
    e.preventDefault();
    openLightbox(item);
  });

  document.addEventListener('keydown', function (e) {
    var item = e.target.closest && e.target.closest('[data-ed-lb-src]');
    if (item && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      openLightbox(item);
      return;
    }

    if (!lb || !lb.classList.contains('is-open')) return;

    if (e.key === 'Escape') {
      closeLightbox();
    } else if (e.key === 'ArrowLeft') {
      step(-1);
    } else if (e.key === 'ArrowRight') {
      step(1);
    } else if (e.key === 'Tab') {
      // Trap focus inside the overlay.
      var focusable = Array.prototype.filter.call(
        lb.querySelectorAll('button:not([hidden])'),
        function (el) {
          return el.offsetParent !== null;
        }
      );
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  /* ----------------------------------------------------------------------
     Video

     The native control bar is the least premium thing on a page like this,
     so it stays hidden behind a poster until the visitor actually plays.
     ---------------------------------------------------------------------- */

  function initVideos(root) {
    var wraps = (root || document).querySelectorAll('[data-ed-video]:not([data-ed-video-bound])');

    Array.prototype.forEach.call(wraps, function (wrap) {
      wrap.setAttribute('data-ed-video-bound', '');

      var video = wrap.querySelector('video');
      var trigger = wrap.querySelector('[data-ed-video-play]');
      if (!video || !trigger) return;

      trigger.addEventListener('click', function () {
        video.setAttribute('controls', '');
        wrap.classList.add('is-playing');
        var played = video.play();
        if (played && played.catch) {
          played.catch(function () {
            /* Autoplay policy blocked it — the native controls are showing. */
          });
        }
        video.focus({ preventScroll: true });
      });

      video.addEventListener('pause', function () {
        if (video.currentTime === 0 || video.ended) wrap.classList.remove('is-playing');
      });

      video.addEventListener('ended', function () {
        wrap.classList.remove('is-playing');
        video.removeAttribute('controls');
      });
    });
  }

  /* ----------------------------------------------------------------------
     Slideshow

     The track is a CSS scroll-snap container, so touch swipe, trackpad and
     momentum are the browser's job. Everything here just drives scrollLeft
     and keeps the filmstrip, counter and caption in sync.

     Each slide is exactly 100% of the track's width, so slide N always sits
     at scrollLeft = N * track.clientWidth. No offset measuring needed.
     ---------------------------------------------------------------------- */

  function initSlideshows(root) {
    var shows = (root || document).querySelectorAll(
      '[data-ed-slideshow]:not([data-ed-slideshow-bound])'
    );

    Array.prototype.forEach.call(shows, function (show) {
      show.setAttribute('data-ed-slideshow-bound', '');

      var track = show.querySelector('[data-edgs-track]');
      if (!track) return;

      var slides = Array.prototype.slice.call(show.querySelectorAll('[data-edgs-slide]'));
      if (slides.length < 2) return;

      var thumbs = Array.prototype.slice.call(show.querySelectorAll('[data-edgs-thumb]'));
      var film = show.querySelector('[data-edgs-film]');
      var counter = show.querySelector('[data-edgs-current]');
      var captionOut = show.querySelector('[data-edgs-caption-out]');
      var prevBtn = show.querySelector('[data-edgs-prev]');
      var nextBtn = show.querySelector('[data-edgs-next]');

      var index = 0;
      var behavior = prefersReduced ? 'auto' : 'smooth';

      function paint() {
        thumbs.forEach(function (t, n) {
          var on = n === index;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });

        if (counter) counter.textContent = pad(index + 1);

        if (captionOut) {
          captionOut.textContent = slides[index].getAttribute('data-edgs-caption') || '';
        }

        // Keep the active thumbnail inside the filmstrip's viewport.
        if (film && thumbs[index]) {
          var t = thumbs[index];
          var target = t.offsetLeft - film.clientWidth / 2 + t.offsetWidth / 2;
          var max = film.scrollWidth - film.clientWidth;
          film.scrollTo({ left: Math.max(0, Math.min(target, max)), behavior: behavior });
        }
      }

      function goTo(i, jump) {
        // Wrap at both ends. A wrap is a long way to travel, so it snaps
        // rather than sliding the whole width of the set.
        var wrapped = false;
        if (i < 0) {
          i = slides.length - 1;
          wrapped = true;
        } else if (i >= slides.length) {
          i = 0;
          wrapped = true;
        }

        index = i;
        track.scrollTo({
          left: track.clientWidth * index,
          behavior: wrapped || jump ? 'auto' : behavior
        });
        paint();
      }

      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          goTo(index - 1);
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          goTo(index + 1);
        });
      }

      thumbs.forEach(function (t, n) {
        t.addEventListener('click', function () {
          goTo(n);
        });
      });

      // Manual swipe / trackpad: read the index back off scrollLeft.
      var raf = null;
      track.addEventListener(
        'scroll',
        function () {
          if (raf) return;
          raf = requestAnimationFrame(function () {
            raf = null;
            if (!track.clientWidth) return;
            var i = Math.round(track.scrollLeft / track.clientWidth);
            i = Math.max(0, Math.min(slides.length - 1, i));
            if (i !== index) {
              index = i;
              paint();
            }
          });
        },
        { passive: true }
      );

      // Arrow keys, but only while focus is inside this gallery — otherwise
      // they would fight page scrolling.
      show.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        // The lightbox owns the arrows while it is open.
        if (lb && lb.classList.contains('is-open')) return;
        e.preventDefault();
        goTo(index + (e.key === 'ArrowRight' ? 1 : -1));
      });

      // A resize changes clientWidth, so the current slide has to be re-pinned.
      var resizeTimer = null;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          goTo(index, true);
        }, 150);
      });

      paint();
    });
  }

  /* ----------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */

  function refresh(root) {
    initReveals(root);
    initVideos(root);
    initSlideshows(root);
  }

  window.MPEditorial = { refresh: refresh, closeLightbox: closeLightbox };

  refresh(document);

  // Theme editor re-renders a section in place.
  document.addEventListener('shopify:section:load', function (e) {
    refresh(e.target);
  });
  document.addEventListener('shopify:section:unload', closeLightbox);
})();
